<i18n src="@/constants/interaction/locales.json"></i18n>
<i18n src="@/constants/element/locales.json"></i18n>
<i18n src="@/constants/conversation/locales.json"></i18n>

<i18n src="./resources/locales.json"></i18n>

<template>
  <v-lazy v-model="isActive" :options="{ threshold: 0.5 }" min-height="36" transition="fade-transition">
    <div v-intersect="onIntersect" class="conversation-message" :class="position" :dark="$vuetify.theme.dark">
      <div class="avatar-container">
        <EcsAvatar
          v-if="messageOriginator.contactType"
          :contact-id="isAgent ? null : messageOriginator.id"
          :agent-id="isAgent ? messageOriginator.id : null"
          :alt="avatarAlt"
          :size="32"
        />
      </div>
      <div class="body-container">
        <div class="message-text">
          <v-icon v-if="hasIcon" class="icon" small>{{ message.icon }}</v-icon>
          <span v-if="isSystemMessage">
            {{ systemMessage }}
          </span>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span v-else v-html="parsedMessageContent"></span>
        </div>
        <div class="sent-date">
          <span v-if="isSystemMessage">{{ $t('system') }}</span>
          <span v-else>{{ messageOriginator.name }}</span>
          &#xb7; {{ timeAgo }}
        </div>
      </div>
    </div>
  </v-lazy>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import { MESSAGE_TYPE } from '@/constants/conversation/constants';
import { ELEMENT_STATE } from '@/constants/element/constants';
import EcsAvatar from '@/components/ecs-base-interaction-components/ecs-avatar/EcsAvatar';
import { parseMessage } from '@/utils';
import { CONTACT_TYPE } from '@/constants/contactConstants';

export default {
  name: 'EcsConversationMessage',

  components: {
    EcsAvatar
  },

  props: {
    message: {
      type: Object,
      default() {
        return {};
      }
    },
    messageIndex: {
      type: Number,
      default: 0
    },
    messages: {
      type: Array,
      default() {
        return [];
      }
    },
    receptor: {
      type: Object,
      default() {
        return {};
      },
      required: true
    },
    isSms: {
      type: Boolean,
      required: true
    }
  },

  data() {
    return {
      isActive: false,
      timeAgo: '',
      intersectObserver: null
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),

    ...mapGetters('CONTACTS', ['getContact']),

    isAgent() {
      return [CONTACT_TYPE.agent, CONTACT_TYPE.supervisor, CONTACT_TYPE.enterprise].includes(
        this.messageOriginator.contactType
      );
    },

    /**
     * Check context info for i18nKey using oldState.
     **/
    computedI18nKey() {
      const { i18nKey, oldState } = this.message;
      const finalI18n = {
        key: i18nKey,
        params: {
          name: this.receptor.name
        }
      };

      // Conversation was on hold for {duration}
      if (oldState === ELEMENT_STATE.diverted && i18nKey === ELEMENT_STATE.active) {
        finalI18n.key = `from-state-to-state.${oldState}-${i18nKey}`;
        finalI18n.params.duration = this.getDurationFrom(oldState);
      }

      // Conference was on hold for {duration}
      if (oldState === ELEMENT_STATE.conferencedDiverted && i18nKey === ELEMENT_STATE.conferenced) {
        finalI18n.key = `from-state-to-state.${oldState}-${i18nKey}`;
        finalI18n.params.duration = this.getDurationFrom(oldState);
      }

      // You are now chatting with {name}.
      if (oldState === ELEMENT_STATE.activating && i18nKey === ELEMENT_STATE.active) {
        finalI18n.key = `from-state-to-state.${oldState}-${i18nKey}`;
      }

      // Your conversation is now active.
      if (oldState === ELEMENT_STATE.created && i18nKey === ELEMENT_STATE.active) {
        finalI18n.key = `from-state-to-state.${oldState}-${i18nKey}`;
      }

      // {name} declined the conversation.
      if (oldState === ELEMENT_STATE.activating && i18nKey === ELEMENT_STATE.abandoned) {
        finalI18n.key = `from-state-to-state.${oldState}-${i18nKey}`;
      }

      return this.$t(finalI18n.key, finalI18n.params);
    },

    isCurrentAgentMessage() {
      return this.messageOriginator.id === this.loggedInAgent.id || this.senderSms();
    },

    isSystemMessage() {
      return this.message.type === MESSAGE_TYPE.system;
    },

    parsedMessageContent() {
      return parseMessage(this.message.content);
    },

    position() {
      if (this.isSystemMessage && !this.message.content) {
        return 'centered-message';
      } else {
        return this.isCurrentAgentMessage || this.isSystemMessage ? 'right-message' : 'left-message';
      }
    },

    messageOriginator() {
      let originator = this.message.originator;

      if (this.isSms && !originator) {
        originator =
          this.message.senderName === this.loggedInAgent.name
            ? { id: this.loggedInAgent.id, name: this.message.senderName }
            : { id: '', name: this.message.senderName };
      }

      return this.getContact(originator.id) || originator;
    },

    hasIcon() {
      return this.message.icon && !this.message.content;
    },

    systemMessage() {
      return this.message.content || this.computedI18nKey;
    },

    avatarAlt() {
      return this.isSystemMessage ? this.$t('system') : this.messageOriginator.name;
    }
  },

  watch: {
    isActive() {
      clearInterval(this.timerReference);
      if (this.isActive) {
        this.updateTimeAgo();
      }
    }
  },

  created() {
    this.timeAgo = moment(this.message.timestamp).fromNow();
    this.updateTimeAgo();
  },

  beforeDestroy() {
    if (this.intersectObserver) {
      this.intersectObserver.disconnect();
    }
    clearInterval(this.timerReference);
  },

  methods: {
    onIntersect(entries, observer) {
      this.intersectObserver = observer;
      if (entries[0]) {
        this.isActive = entries[0].isIntersecting;
      }
    },
    updateTimeAgo() {
      this.timerReference = setInterval(() => {
        this.timeAgo = moment(this.message.timestamp).fromNow();
      }, 60000);
    },

    getDurationFrom(oldState) {
      const x = moment(this.message.timestamp);
      const y = moment(this.lastSystemMessage(oldState).timestamp);
      const duration = moment.duration(x.diff(y));
      const hh = duration.get('hours');
      const mm = duration.get('minutes');
      const ss = duration.get('seconds') || '0';
      const hhText = hh ? `${hh}h` : '';
      const mmText = mm ? `${mm}m` : '';
      const ssText = ss ? `${ss}s` : '';
      return `${hhText} ${mmText} ${ssText}`;
    },

    lastSystemMessage(lookForKey) {
      let i = this.messageIndex;
      if (this.messageIndex === 0) {
        return this.messages[0];
      }
      --i;
      while (i > 0) {
        if (this.messages[i].i18nKey === lookForKey) {
          break;
        }
        --i;
      }
      return this.messages[i];
    },

    senderSms() {
      const receptorName = this.receptor.name || this.receptor.firstName;
      return this.isSms && !this.message.originator && this.message.senderName !== receptorName;
    }
  }
};
</script>

<style src="./scoped.css" scoped />
